import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Input } from '~/components/primitives';
import { fetchSalesOrder, updateSalesOrder } from '~/lib/api/sales-orders';
import shared from './_auth._shared.module.css';

/**
 * EFE-3 Pass 91 (2026-05-25) — Sales Order edit page (header fields only).
 * Lines используют inline CRUD на detail page. Editable только в draft/quotation.
 */

export const Route = createFileRoute('/_auth/sales-orders/$id/edit')({
  component: SoEdit,
});

function SoEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({ queryKey: ['sales-order', id], queryFn: () => fetchSalesOrder(id) });

  const [form, setForm] = useState({
    order_date: '',
    delivery_expected_date: '',
    contract_number: '',
    contract_date: '',
    notes: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const so = query.data as Record<string, unknown>;
      setForm({
        order_date: String(so.order_date ?? '').slice(0, 10),
        delivery_expected_date: String(so.delivery_expected_date ?? '').slice(0, 10),
        contract_number: String((so.contract_number as string | undefined) ?? ''),
        contract_date: String(so.contract_date ?? '').slice(0, 10),
        notes: String((so.notes as string | undefined) ?? ''),
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    mutationFn: () => {
      const so = query.data as Record<string, unknown>;
      return updateSalesOrder(id, {
        counterparty_id: String(so.counterparty_id),
        currency: String(so.currency ?? 'KZT') as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY',
        order_date: form.order_date,
        delivery_expected_date: form.delivery_expected_date || null,
        contract_number: form.contract_number || null,
        contract_date: form.contract_date || null,
        notes: form.notes || null,
      });
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.so_toast_updated() });
      void qc.invalidateQueries({ queryKey: ['sales-order', id] });
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
      void navigate({ to: '/sales-orders/$id', params: { id } });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.so_toast_update_failed(),
        description: err.message,
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;
  const so = query.data as Record<string, unknown>;
  const status = String(so.status ?? 'unknown');
  const editable = status === 'draft' || status === 'quotation';
  const code = String(so.code ?? id.slice(0, 8));

  if (!editable) {
    return (
      <div>
        <PageHeader
          breadcrumbs={
            <Breadcrumbs
              items={[
                { label: m.nav_sales_orders(), to: '/sales-orders' },
                { label: code, to: `/sales-orders/${id}` },
                { label: m.common_button_edit() },
              ]}
            />
          }
          title={m.so_edit_locked_title()}
          description={m.so_edit_locked_hint({ state: status })}
        />
        <Button
          variant="secondary"
          onClick={() => navigate({ to: '/sales-orders/$id', params: { id } })}
        >
          {m.common_button_back()}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_sales_orders(), to: '/sales-orders' },
              { label: code, to: `/sales-orders/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={`${m.common_button_edit()}: ${code}`}
      />
      <div className={shared.formColumn480}>
        <Input
          label={m.so_col_order_date()}
          required
          type="date"
          value={form.order_date}
          onChange={(e) => setForm({ ...form, order_date: e.target.value })}
        />
        <Input
          label={m.so_col_delivery_date()}
          type="date"
          value={form.delivery_expected_date}
          onChange={(e) => setForm({ ...form, delivery_expected_date: e.target.value })}
        />
        <Input
          label={m.so_form_contract_number()}
          value={form.contract_number}
          onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
        />
        <Input
          label={m.so_form_contract_date()}
          type="date"
          value={form.contract_date}
          onChange={(e) => setForm({ ...form, contract_date: e.target.value })}
        />
        <Input
          label={m.wo_field_remarks()}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/sales-orders/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={!form.order_date}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
